import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Paper, 
  Alert,
  CircularProgress 
} from '@mui/material';
import { API, graphqlOperation } from 'aws-amplify';
import { format } from 'date-fns';
import { controlTableByClient, listControlTables } from '../../../../graphql/queries';
import StatusChip from '../common/StatusChip';
import TaskStatusSummary from './TaskStatusSummary';

const MonitoringDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [taskStatusCounts, setTaskStatusCounts] = useState({
    PENDING: 0,
    PROCESSING: 0,
    COMPLETED: 0,
    FAILED: 0,
    VALIDATED: 0
  });

  useEffect(() => {
    fetchDashboardData();
    
    // Polling for updates every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Get recent tasks      
      const recentTasksResponse = await API.graphql(graphqlOperation( controlTableByClient, {
        clientId: 'grm',
        sortDirection: 'DESC',
        limit: 5
      }));
      // const recentTasksResponse = await API.graphql(graphqlOperation(listControlTables, {
      //   limit: 5,
      //   sortDirection: 'DESC',
      //   sort: { field: 'lastUpdated', direction: 'desc' }
      // }));
      console.log('Recent Tasks Response:', recentTasksResponse);
      const tasks = recentTasksResponse.data.controlTableByClient.items;
      setRecentTasks(tasks);
      
      // Calculate task status counts
      const counts = {
        PENDING: 0,
        PROCESSING: 0,
        COMPLETED: 0,
        FAILED: 0,
        VALIDATED: 0
      };
      
      // We'll make separate queries for status counts to be more efficient
      const pendingResponse = await API.graphql(graphqlOperation(listControlTables, {
        filter: { status: { eq: 'PENDING' } },
        limit: 5
      }));
      counts.PENDING = pendingResponse.data.listControlTables.items.length;
      
      const processingResponse = await API.graphql(graphqlOperation(listControlTables, {
        filter: { status: { eq: 'PROCESSING' } },
        limit: 5
      }));
      counts.PROCESSING = processingResponse.data.listControlTables.items.length;
      
      const completedResponse = await API.graphql(graphqlOperation(listControlTables, {
        filter: { status: { eq: 'COMPLETED' } },
        limit: 5
      }));
      counts.COMPLETED = completedResponse.data.listControlTables.items.length;
      
      const failedResponse = await API.graphql(graphqlOperation(listControlTables, {
        filter: { status: { eq: 'FAILED' } },
        limit: 5
      }));
      counts.FAILED = failedResponse.data.listControlTables.items.length;
      
      const validatedResponse = await API.graphql(graphqlOperation(listControlTables, {
        filter: { status: { eq: 'VALIDATED' } },
        limit: 5
      }));
      counts.VALIDATED = validatedResponse.data.listControlTables.items.length;
      
      setTaskStatusCounts(counts);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load monitoring data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && recentTasks.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Task Processing Status
      </Typography>
      
      <TaskStatusSummary taskStatusCounts={taskStatusCounts} />
      
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Recent Task Activity
      </Typography>
      
      <Paper sx={{ overflow: 'auto' }}>
        <Box component="table" sx={{ 
          width: '100%', 
          borderCollapse: 'collapse'
        }}>
          <Box component="thead">
            <Box component="tr" sx={{ 
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }}>
              <Box component="th" sx={{ p: 2, textAlign: 'left' }}>Task ID</Box>
              <Box component="th" sx={{ p: 2, textAlign: 'left' }}>Status</Box>
              <Box component="th" sx={{ p: 2, textAlign: 'left' }}>Data Category</Box>
              <Box component="th" sx={{ p: 2, textAlign: 'left' }}>Start Time</Box>
              <Box component="th" sx={{ p: 2, textAlign: 'left' }}>Duration</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {recentTasks.map(task => (
              <Box component="tr" key={task.taskId} sx={{ 
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                cursor: 'pointer'
              }} onClick={() => {/* Handle task click */}}>
                <Box component="td" sx={{ p: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                  {task.taskId}
                </Box>
                <Box component="td" sx={{ p: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                  <StatusChip status={task.status} />
                </Box>
                <Box component="td" sx={{ p: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                  {task.dataCategory}
                </Box>
                <Box component="td" sx={{ p: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                  {task.startTime ? format(new Date(task.startTime), 'yyyy-MM-dd HH:mm:ss') : '-'}
                </Box>
                <Box component="td" sx={{ p: 2, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}>
                  {task.processingDuration ? `${Math.round(task.processingDuration / 1000)}s` : '-'}
                </Box>
              </Box>
            ))}
            
            {recentTasks.length === 0 && (
              <Box component="tr">
                <Box component="td" colSpan={5} sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No recent task activity found
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default MonitoringDashboard;